import React from "react";
import "./PhaseLinearNode.css";
import { convertSecondsToDurationString, convertSecondsToDurationString_Text } from "../../utils/time";
import { PhaseType } from "../../config/config";
import MuteButton from "../MuteButton";

interface PhaseLinearNodeProps {
    name: string;
    duration: number;
    remainingTime: number;
    elapsedTime: number;
    isActive: boolean;
    isIncomingTransition: boolean;
    phaseType?: PhaseType;
    transitionProgress: number;
    barColor: string;
    borderColor: string;
    bgColor: string;
    roomColor: string;
    inside?: boolean;
    isPlayingAudio?: boolean;
}

const SMALL = 20;
const LARGE = 32;

export const PhaseLinearNode: React.FC<PhaseLinearNodeProps> = ({
    name,
    duration,
    remainingTime,
    elapsedTime,
    isActive,
    isIncomingTransition,
    phaseType,
    transitionProgress,
    barColor,
    borderColor,
    bgColor,
    roomColor,
    inside,
    isPlayingAudio,
}) => {
    // Radius bestimmen
    let radius = isActive ? LARGE : SMALL;
    const isOutgoingTransition = isActive && transitionProgress > 0;

    if (isIncomingTransition && transitionProgress < 1) {
        // incoming: SMALL → LARGE
        radius = SMALL + (LARGE - SMALL) * transitionProgress;
    } else if (isOutgoingTransition && transitionProgress < 1) {
        // outgoing: nur während der letzten 2s schrumpfen
        radius = LARGE - (LARGE - SMALL) * transitionProgress;
    }
    // wenn transitionProgress===1 und isActive, bleibt radius=LARGE

    // Progress 0…1
    const progress = Math.max(0, Math.min(elapsedTime / duration, 1));

    // regulate the displayed progress to avoid weird shapes 
    const minDisplayedProgress = 0.03; // minimaler Fortschritt, der angezeigt wird, damit der linke Arc nicht so komisch rund aussieht.
    const displayedProgress =
        isActive
            ? minDisplayedProgress + progress * (1 - minDisplayedProgress)
            : isIncomingTransition
                ? minDisplayedProgress
                : progress; //either 0 or 1, no need to adjust

    //transition opacity from 0 to 1 in the last 20% of the overall 2s animation
    const opacityProgress = transitionProgress >= 1
        ? 0
        : transitionProgress > 0.8
            ? (transitionProgress - 0.8) * 5
            : 0;
    const opacityStyle = isIncomingTransition
        ? { opacity: opacityProgress }
        : isOutgoingTransition
            ? { opacity: 1 - opacityProgress }
            : {};

    // Restzeit-String (MM:SS)
    const remainingTimeString = convertSecondsToDurationString(remainingTime ?? duration);
    const remainingTimeMinutes = convertSecondsToDurationString_Text(remainingTime ?? duration);

    return (
        <div className={
            "phase-node " + (isActive ? "active" : remainingTime > 0 ? "upcoming" : "past")
        }>
            <div className="layer bg-border">
                <svg
                    className="phase-node__svg"
                    width={2 * LARGE}
                    height={2 * LARGE}
                >
                    <g transform={`translate(${LARGE},${LARGE})`}>
                        <circle
                            className="phase-node__bg-circle"
                            r={radius}
                            fill={borderColor}
                        />
                    </g>
                </svg>
            </div>
            <div className="layer bg-inner">
                <svg
                    className="phase-node__svg"
                    width={2 * LARGE}
                    height={2 * LARGE}
                >
                    <g transform={`translate(${LARGE},${LARGE})`}>
                        <circle
                            className="phase-node__bg-circle"
                            r={radius - 2}
                            fill={bgColor}
                        />
                    </g>
                </svg>
            </div>
            {displayedProgress > 0 &&
                <div className="layer progress"
                    style={opacityStyle}
                >
                    <svg className="phase-node__svg" width={2 * LARGE} height={2 * LARGE}>
                        <g transform={`translate(${LARGE},${LARGE})`}>
                            {(() => {
                                const r = radius - 5;


                                // Start- und Endwinkel in Bogenmaß
                                const startAngle = Math.PI * (1 - displayedProgress);
                                const endAngle = Math.PI * (1 + displayedProgress);
                                // Punktkoordinaten
                                const x1 = r * Math.cos(startAngle);
                                const y1 = r * Math.sin(startAngle);
                                const x2 = r * Math.cos(endAngle);
                                const y2 = r * Math.sin(endAngle);
                                // large-arc-flag wenn größer als Halbkreis
                                const largeArc = displayedProgress > 0.5 ? 1 : 0;
                                const d = `
                    M ${x1},${y1}
                    A ${r},${r} 0 ${largeArc} 1 ${x2},${y2}
                `;
                                return (
                                    <path
                                        d={d.trim()}
                                        fill="none"
                                        stroke={barColor}
                                        strokeWidth={6}
                                        strokeLinecap="round"
                                    />
                                );
                            })()}
                        </g>
                    </svg>
                </div>
            }
            <div className="phase-node__label">{name}</div>
            {(isActive || isIncomingTransition) && (
                <div
                    className="phase-node__time"
                    style={opacityStyle}
                >
                    {remainingTimeString}
                </div>
            )}
            {inside && (isActive || isIncomingTransition) && (
                <div className="phase-node__mute-button"
                    style={opacityStyle}>
                    <MuteButton
                        isPlayingAudio={!!isPlayingAudio}
                        color={roomColor}
                    />
                </div>
            )}
            {!isActive && phaseType === "focus" && remainingTime > 0 && (
                <div className="phase-node__time">{remainingTimeMinutes}</div>
            )}

        </div>
    );
};

export default PhaseLinearNode;
